// Phase 2.2 Lite — Floating private-review header. Pinned to the top of
// the public review page. Subtle dark pill, two actions, status badge.

import React, { useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Loader2, Check, MessageSquare, Eye } from 'lucide-react';
import RequestChangesModal from './RequestChangesModal';

const API = process.env.REACT_APP_BACKEND_URL;

const STATUS_LABELS = {
  draft:               'Draft',
  sent_for_review:     'Sent for review',
  approved:            'Approved',
  changes_requested:   'Changes requested',
};

const STATUS_TONES = {
  draft:               'bg-white/15',
  sent_for_review:     'bg-sky-400/30',
  approved:            'bg-emerald-400/30',
  changes_requested:   'bg-amber-400/30',
};

export default function ReviewHeader({ status, eventSlug, token, onStatusChange }) {
  const [approving, setApproving] = useState(false);
  const [requestOpen, setRequestOpen] = useState(false);

  const handleApprove = async () => {
    setApproving(true);
    try {
      await axios.post(
        `${API}/api/public/review/${eventSlug}/approve`,
        { token },
      );
      toast.success('Review approved');
      onStatusChange?.('approved');
    } catch {
      toast.error('Could not approve');
    } finally {
      setApproving(false);
    }
  };

  const isFinal = status === 'approved' || status === 'changes_requested';

  return (
    <>
      <div
        className="fixed top-3 left-3 right-3 sm:left-4 sm:right-auto z-50 inline-flex items-center gap-2 px-3 py-2 rounded-full bg-black/80 text-white text-[11px] shadow-lg backdrop-blur"
        data-testid="review-header"
      >
        <Eye className="w-3.5 h-3.5 opacity-80" />
        <span className="font-medium" data-testid="review-header-label">
          Private review
        </span>
        <span
          className={`px-2 py-0.5 rounded-full text-[10px] uppercase tracking-wider ${STATUS_TONES[status] || 'bg-white/15'}`}
          data-testid="review-status-badge"
        >
          {STATUS_LABELS[status] || status}
        </span>
        <div className="flex items-center gap-1 ml-1.5 pl-2 border-l border-white/15">
          <button
            onClick={handleApprove}
            disabled={approving || status === 'approved'}
            className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-medium bg-emerald-500/80 hover:bg-emerald-500 disabled:opacity-40 disabled:cursor-not-allowed"
            data-testid="review-approve-btn"
          >
            {approving ? <Loader2 className="w-3 h-3 animate-spin" /> : <Check className="w-3 h-3" />}
            Approve
          </button>
          <button
            onClick={() => setRequestOpen(true)}
            className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-medium bg-white/15 hover:bg-white/25"
            data-testid="review-request-changes-btn"
          >
            <MessageSquare className="w-3 h-3" />
            Request changes
          </button>
        </div>
      </div>

      {isFinal && (
        <div
          className="fixed top-14 left-3 right-3 sm:left-4 sm:right-auto z-40 max-w-xs text-[10px] text-white/70 italic"
          data-testid="review-status-helper"
        >
          {status === 'approved' && 'Approved · the designer has been notified.'}
          {status === 'changes_requested' && 'Changes requested · the designer will see your note in their Studio.'}
        </div>
      )}

      {requestOpen && (
        <RequestChangesModal
          eventSlug={eventSlug}
          token={token}
          onClose={() => setRequestOpen(false)}
          onSubmitted={() => onStatusChange?.('changes_requested')}
        />
      )}
    </>
  );
}
